import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Link, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import SupportChatWidget from './components/common/SupportChatWidget';
import { SITE_HOME, SITE_LOGIN } from './config/siteLinks';

// Registration is temporarily disabled — this replaces the /register route
// without touching StudentRegister.js, so it can be re-enabled later by
// swapping this element back for <StudentRegister />.
// Hard navigation, not <Navigate>: / is served by IIS as the static marketing
// site and has no React route to resolve to.
const RedirectToHome = () => {
  React.useEffect(() => { window.location.replace(SITE_HOME); }, []);
  return null;
};

const RegistrationClosed = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-950 to-secondary-950 flex items-center justify-center px-4">
    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
      <h1 className="text-xl font-bold text-slate-800 mb-2">Registration Temporarily Closed</h1>
      <p className="text-slate-500 text-sm mb-6">New student self-registration isn't available right now. Please contact your school administrator for an account.</p>
      <a href={SITE_HOME} className="inline-block px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium text-sm">
        Back to Home
      </a>
    </div>
  </div>
);

// Wraps lazy() so a stale tab that fails to fetch a chunk (because a newer
// deploy replaced the build's hashed filenames since the tab was opened)
// self-heals with a single automatic reload instead of showing a crash screen.
const RELOAD_FLAG = 'chunk-reload-attempted';
const lazyWithReload = (factory) => lazy(() =>
  factory().catch((error) => {
    const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk .* failed/.test(error?.message || '');
    if (isChunkError && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, 'true');
      window.location.reload();
      return new Promise(() => {}); // page is reloading; never resolve
    }
    throw error;
  })
);

// Lazy load pages for performance
const WelcomeScreen = lazyWithReload(() => import('./pages/WelcomeScreen'));
const SharedReport = lazyWithReload(() => import('./pages/public/SharedReport'));
const RoleInfo = lazyWithReload(() => import('./pages/RoleInfo'));
const StudentLogin = lazyWithReload(() => import('./pages/auth/StudentLogin'));
const TeacherLogin = lazyWithReload(() => import('./pages/auth/TeacherLogin'));
const AdminLogin = lazyWithReload(() => import('./pages/auth/AdminLogin'));
const SuperAdminLogin = lazyWithReload(() => import('./pages/auth/SuperAdminLogin'));
const AccountantLogin = lazyWithReload(() => import('./pages/auth/AccountantLogin'));
const ForgotPassword = lazyWithReload(() => import('./pages/auth/ForgotPassword'));
const ForceChangePassword = lazyWithReload(() => import('./pages/auth/ForceChangePassword'));

// Student pages
const StudentDashboard = lazyWithReload(() => import('./pages/student/Dashboard'));
const AssignedTests = lazyWithReload(() => import('./pages/student/AssignedTests'));
const SubjectSelection = lazyWithReload(() => import('./pages/student/SubjectSelection'));
const ChapterSelection = lazyWithReload(() => import('./pages/student/ChapterSelection'));
const BuildYourTest = lazyWithReload(() => import('./pages/student/BuildYourTest'));
const DailyQuiz = lazyWithReload(() => import('./pages/student/DailyQuiz'));
const TestInstructions = lazyWithReload(() => import('./pages/student/TestInstructions'));
const TestScreen = lazyWithReload(() => import('./pages/student/TestScreen'));
const Results = lazyWithReload(() => import('./pages/student/Results'));
const AttemptResult = lazyWithReload(() => import('./pages/student/AttemptResult'));
const SolutionReview = lazyWithReload(() => import('./pages/student/SolutionReview'));
const PerformanceAnalysis = lazyWithReload(() => import('./pages/student/PerformanceAnalysis'));
const StudentProfile = lazyWithReload(() => import('./pages/student/StudentProfile'));
const StudentEBooks = lazyWithReload(() => import('./pages/student/EBooks'));
const StudentQuestionPapers = lazyWithReload(() => import('./pages/student/QuestionPapers'));

// Teacher pages
const TeacherDashboard = lazyWithReload(() => import('./pages/teacher/TeacherDashboard'));
const CreateTest = lazyWithReload(() => import('./pages/teacher/CreateTest'));
const TeacherUploadQuestions = lazyWithReload(() => import('./pages/teacher/TeacherUploadQuestions'));
const CustomTest = lazyWithReload(() => import('./pages/teacher/CustomTest'));
const EditTest = lazyWithReload(() => import('./pages/teacher/EditTest'));
const ViewTest = lazyWithReload(() => import('./pages/teacher/ViewTest'));
const TeacherTests = lazyWithReload(() => import('./pages/teacher/TeacherTests'));
const TeacherStudents = lazyWithReload(() => import('./pages/teacher/TeacherStudents'));
const TeacherReports = lazyWithReload(() => import('./pages/teacher/TeacherReports'));
const TeacherAttemptDetail = lazyWithReload(() => import('./pages/teacher/TeacherAttemptDetail'));
const TeacherSchedule = lazyWithReload(() => import('./pages/teacher/TeacherSchedule'));
const TeacherAnalytics = lazyWithReload(() => import('./pages/teacher/TeacherAnalytics'));
const TeacherAttendance = lazyWithReload(() => import('./pages/teacher/TeacherAttendance'));
const TeacherProfile = lazyWithReload(() => import('./pages/teacher/TeacherProfile'));

// Admin pages
const AdminDashboard = lazyWithReload(() => import('./pages/admin/AdminDashboard'));
const AdminCurriculum = lazyWithReload(() => import('./pages/admin/AdminCurriculum'));
const Schools = lazyWithReload(() => import('./pages/admin/Schools'));
const Teachers = lazyWithReload(() => import('./pages/admin/Teachers'));
const AdminStudents = lazyWithReload(() => import('./pages/admin/AdminStudents'));
const Batches = lazyWithReload(() => import('./pages/admin/Batches'));
const AdminSchedule = lazyWithReload(() => import('./pages/admin/AdminSchedule'));
const AdminAttemptDetail = lazyWithReload(() => import('./pages/admin/AdminAttemptDetail'));
const QuestionBank = lazyWithReload(() => import('./pages/admin/QuestionBank'));
const AddQuestion = lazyWithReload(() => import('./pages/admin/AddQuestion'));
const PendingQuestionReview = lazyWithReload(() => import('./pages/admin/PendingQuestionReview'));
const EditQuestion = lazyWithReload(() => import('./pages/admin/EditQuestion'));
const AdminTests = lazyWithReload(() => import('./pages/admin/AdminTests'));
const AdminBulkUploadQuestionsAndTests = lazyWithReload(() => import('./pages/admin/AdminBulkUploadQuestionsAndTests'));
const AdminReports = lazyWithReload(() => import('./pages/admin/AdminReports'));
const Analytics = lazyWithReload(() => import('./pages/admin/Analytics'));
const AdminProfile = lazyWithReload(() => import('./pages/admin/AdminProfile'));
const AdminBilling = lazyWithReload(() => import('./pages/admin/Billing'));
const AuditLog = lazyWithReload(() => import('./pages/admin/AuditLog'));
const AdminAttendance = lazyWithReload(() => import('./pages/admin/AdminAttendance'));
const AdminAccountants = lazyWithReload(() => import('./pages/admin/AdminAccountants'));

// Accountant pages (Finance module)
const AccountantDashboard = lazyWithReload(() => import('./pages/accountant/AccountantDashboard'));
const FeeStructures = lazyWithReload(() => import('./pages/accountant/FeeStructures'));
const StudentFees = lazyWithReload(() => import('./pages/accountant/StudentFees'));
const CollectionsReport = lazyWithReload(() => import('./pages/accountant/CollectionsReport'));
const Receipts = lazyWithReload(() => import('./pages/accountant/Receipts'));
const PendingDues = lazyWithReload(() => import('./pages/accountant/PendingDues'));
const PaymentCorrections = lazyWithReload(() => import('./pages/accountant/PaymentCorrections'));
const AccountantProfile = lazyWithReload(() => import('./pages/accountant/AccountantProfile'));

// Instructor pages — Create/Custom Test, My Tests, Edit/View Test, and the
// attempt-detail drill-down are the SAME components already lazy-imported
// above for teacher/admin (CreateTest, CustomTest, TeacherTests, EditTest,
// ViewTest, TeacherAttemptDetail) — reused as-is under /instructor/* below,
// no separate import needed. Only what's genuinely instructor-specific:
const InstructorDashboard = lazyWithReload(() => import('./pages/instructor/InstructorDashboard'));
const InstructorProfile = lazyWithReload(() => import('./pages/instructor/InstructorProfile'));
const InstructorStudents = lazyWithReload(() => import('./pages/instructor/InstructorStudents'));
const InstructorCourses = lazyWithReload(() => import('./pages/instructor/InstructorCourses'));
const InstructorCourseDetail = lazyWithReload(() => import('./pages/instructor/InstructorCourses').then(m => ({ default: m.InstructorCourseDetail })));

// Superadmin pages
const SuperAdminDashboard = lazyWithReload(() => import('./pages/superadmin/SuperAdminDashboard'));
const Customers = lazyWithReload(() => import('./pages/superadmin/Customers'));
const Instructors = lazyWithReload(() => import('./pages/superadmin/Instructors'));
const InstructorDetail = lazyWithReload(() => import('./pages/superadmin/InstructorDetail'));
const CustomerDetail = lazyWithReload(() => import('./pages/superadmin/CustomerDetail'));
const Billing = lazyWithReload(() => import('./pages/superadmin/Billing'));
const SuperAdminProfile = lazyWithReload(() => import('./pages/superadmin/SuperAdminProfile'));
const CurriculumAnalytics = lazyWithReload(() => import('./pages/superadmin/CurriculumAnalytics'));
const ArchivedData = lazyWithReload(() => import('./pages/superadmin/ArchivedData'));
const Admissions = lazyWithReload(() => import('./pages/superadmin/Admissions'));
const IndividualStudents = lazyWithReload(() => import('./pages/superadmin/IndividualStudents'));
const IndividualCourses = lazyWithReload(() => import('./pages/superadmin/IndividualCourses'));
const IndividualPayments = lazyWithReload(() => import('./pages/superadmin/IndividualPayments'));
const SuperAdminEBooks = lazyWithReload(() => import('./pages/superadmin/EBooks'));
const Inquiries = lazyWithReload(() => import('./pages/superadmin/Inquiries'));
const SuperAdminQuestionPapers = lazyWithReload(() => import('./pages/superadmin/QuestionPapers'));
const WhatsAppTest = lazyWithReload(() => import('./pages/superadmin/WhatsAppTest'));
const WhatsAppTemplates = lazyWithReload(() => import('./pages/superadmin/WhatsAppTemplates'));
const WhatsAppAccounts = lazyWithReload(() => import('./pages/superadmin/WhatsAppAccounts'));
const WhatsAppNumbers = lazyWithReload(() => import('./pages/superadmin/WhatsAppNumbers'));
const WhatsAppReports = lazyWithReload(() => import('./pages/superadmin/WhatsAppReports'));
const Permissions = lazyWithReload(() => import('./pages/superadmin/Permissions'));
const SuperAdminUsers = lazyWithReload(() => import('./pages/superadmin/SuperAdminUsers'));
const PlatformAuditLog = lazyWithReload(() => import('./pages/superadmin/PlatformAuditLog'));
const WhatsAppDashboard = lazyWithReload(() => import('./pages/superadmin/WhatsAppDashboard'));
const SupportTickets = lazyWithReload(() => import('./pages/superadmin/SupportTickets'));
const SupportTicketDetail = lazyWithReload(() => import('./pages/superadmin/SupportTicketDetail'));

// Layout wrapper for authenticated routes
const StudentLayout = () => (
  <ProtectedRoute role="student">
    <Outlet />
  </ProtectedRoute>
);

const TeacherLayout = () => (
  <ProtectedRoute role="teacher">
    <Outlet />
    <SupportChatWidget />
  </ProtectedRoute>
);

const AdminLayout = () => (
  <ProtectedRoute role="admin">
    <Outlet />
    <SupportChatWidget />
  </ProtectedRoute>
);

const SuperAdminLayout = () => (
  <ProtectedRoute role="superadmin">
    <Outlet />
  </ProtectedRoute>
);

const AccountantLayout = () => (
  <ProtectedRoute role="accountant">
    <Outlet />
  </ProtectedRoute>
);

const InstructorLayout = () => (
  <ProtectedRoute role="instructor">
    <Outlet />
  </ProtectedRoute>
);

const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<LoadingSpinner fullPage />}>
    {children}
  </Suspense>
);

const router = createBrowserRouter([
  // "/" is deliberately NOT a React route: IIS serves the marketing site there.
  // Declaring it here would let <Link to="/"> resolve client-side and render a
  // React page instead of loading the real site.
  // The marketing funnel's landing link. Renders the same landing page as
  // /welcome but with the enquiry form already open, so a click from a social
  // post goes straight to the form without losing the surrounding site.
  {
    path: '/apply',
    element: <SuspenseWrapper><WelcomeScreen autoOpenEnquiry /></SuspenseWrapper>,
  },
  // /welcome was the old React landing page. It is retired — the marketing
  // site at / replaced it — but the path is kept as a redirect so existing
  // links, bookmarks and search results do not 404.
  {
    path: '/welcome',
    element: <RedirectToHome />,
  },
  {
    path: '/register',
    element: <RegistrationClosed />,
  },
  {
    path: '/shared-report/:token',
    element: <SuspenseWrapper><SharedReport /></SuspenseWrapper>,
  },
  {
    path: '/about/:role',
    element: <SuspenseWrapper><RoleInfo /></SuspenseWrapper>,
  },
  {
    path: '/login/student',
    element: <SuspenseWrapper><StudentLogin /></SuspenseWrapper>,
  },
  {
    path: '/login/teacher',
    element: <SuspenseWrapper><TeacherLogin /></SuspenseWrapper>,
  },
  {
    path: '/login/admin',
    element: <SuspenseWrapper><AdminLogin /></SuspenseWrapper>,
  },
  {
    path: '/login/superadmin',
    element: <SuspenseWrapper><SuperAdminLogin /></SuspenseWrapper>,
  },
  {
    path: '/login/accountant',
    element: <SuspenseWrapper><AccountantLogin /></SuspenseWrapper>,
  },
  {
    path: '/forgot-password',
    element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper>,
  },
  {
    path: '/force-change-password',
    element: <SuspenseWrapper><ForceChangePassword /></SuspenseWrapper>,
  },

  // Student Routes
  {
    path: '/student',
    element: <StudentLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><StudentDashboard /></SuspenseWrapper> },
      { path: 'assigned-tests', element: <SuspenseWrapper><AssignedTests /></SuspenseWrapper> },
      { path: 'subjects', element: <SuspenseWrapper><SubjectSelection /></SuspenseWrapper> },
      { path: 'chapters/:subjectId', element: <SuspenseWrapper><ChapterSelection /></SuspenseWrapper> },
      { path: 'build-test', element: <SuspenseWrapper><BuildYourTest /></SuspenseWrapper> },
      { path: 'daily-quiz', element: <SuspenseWrapper><DailyQuiz /></SuspenseWrapper> },
      { path: 'test-instructions/:testId', element: <SuspenseWrapper><TestInstructions /></SuspenseWrapper> },
      { path: 'results', element: <SuspenseWrapper><Results /></SuspenseWrapper> },
      { path: 'results/:attemptId', element: <SuspenseWrapper><AttemptResult /></SuspenseWrapper> },
      { path: 'solution-review/:attemptId', element: <SuspenseWrapper><SolutionReview /></SuspenseWrapper> },
      { path: 'performance', element: <SuspenseWrapper><PerformanceAnalysis /></SuspenseWrapper> },
      { path: 'ebooks', element: <SuspenseWrapper><StudentEBooks /></SuspenseWrapper> },
      { path: 'question-papers', element: <SuspenseWrapper><StudentQuestionPapers /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><StudentProfile /></SuspenseWrapper> },
    ],
  },
  // Test screen has its own route outside Layout (full screen)
  {
    path: '/student/test/:testId/:attemptId',
    element: (
      <ProtectedRoute role="student">
        <SuspenseWrapper><TestScreen /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Teacher Routes
  {
    path: '/teacher',
    element: <TeacherLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><TeacherDashboard /></SuspenseWrapper> },
      { path: 'create-test', element: <SuspenseWrapper><CreateTest /></SuspenseWrapper> },
      { path: 'custom-test', element: <SuspenseWrapper><CustomTest /></SuspenseWrapper> },
      { path: 'upload-questions', element: <SuspenseWrapper><TeacherUploadQuestions /></SuspenseWrapper> },
      { path: 'tests', element: <SuspenseWrapper><TeacherTests /></SuspenseWrapper> },
      { path: 'tests/:id/edit', element: <SuspenseWrapper><EditTest /></SuspenseWrapper> },
      { path: 'tests/:id/view', element: <SuspenseWrapper><ViewTest /></SuspenseWrapper> },
      { path: 'students', element: <SuspenseWrapper><TeacherStudents /></SuspenseWrapper> },
      { path: 'reports', element: <SuspenseWrapper><TeacherReports /></SuspenseWrapper> },
      { path: 'attempts/:attemptId', element: <SuspenseWrapper><TeacherAttemptDetail /></SuspenseWrapper> },
      { path: 'schedule', element: <SuspenseWrapper><TeacherSchedule /></SuspenseWrapper> },
      { path: 'analytics', element: <SuspenseWrapper><TeacherAnalytics /></SuspenseWrapper> },
      { path: 'attendance', element: <SuspenseWrapper><TeacherAttendance /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><TeacherProfile /></SuspenseWrapper> },
    ],
  },

  // Instructor Routes — Create/Custom Test, My Tests, Edit/View Test, and
  // the attempt-detail page below all reuse the exact same components
  // teacher/admin already use (they're organization_id-scoped, not role-
  // specific — see instructorController.js/routes/teacher.js's comments).
  {
    path: '/instructor',
    element: <InstructorLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><InstructorDashboard /></SuspenseWrapper> },
      { path: 'students', element: <SuspenseWrapper><InstructorStudents /></SuspenseWrapper> },
      { path: 'courses', element: <SuspenseWrapper><InstructorCourses /></SuspenseWrapper> },
      { path: 'courses/:id', element: <SuspenseWrapper><InstructorCourseDetail /></SuspenseWrapper> },
      { path: 'create-test', element: <SuspenseWrapper><CreateTest /></SuspenseWrapper> },
      { path: 'custom-test', element: <SuspenseWrapper><CustomTest /></SuspenseWrapper> },
      { path: 'tests', element: <SuspenseWrapper><TeacherTests /></SuspenseWrapper> },
      { path: 'tests/:id/edit', element: <SuspenseWrapper><EditTest /></SuspenseWrapper> },
      { path: 'tests/:id/view', element: <SuspenseWrapper><ViewTest /></SuspenseWrapper> },
      { path: 'attempts/:attemptId', element: <SuspenseWrapper><TeacherAttemptDetail /></SuspenseWrapper> },
      { path: 'reports', element: <SuspenseWrapper><TeacherReports /></SuspenseWrapper> },
      { path: 'schedule', element: <SuspenseWrapper><TeacherSchedule /></SuspenseWrapper> },
      { path: 'analytics', element: <SuspenseWrapper><TeacherAnalytics /></SuspenseWrapper> },
      { path: 'attendance', element: <SuspenseWrapper><TeacherAttendance /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><InstructorProfile /></SuspenseWrapper> },
    ],
  },

  // Admin Routes
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><AdminDashboard /></SuspenseWrapper> },
      { path: 'schools', element: <SuspenseWrapper><Schools /></SuspenseWrapper> },
      { path: 'teachers', element: <SuspenseWrapper><Teachers /></SuspenseWrapper> },
      { path: 'students', element: <SuspenseWrapper><AdminStudents /></SuspenseWrapper> },
      { path: 'batches', element: <SuspenseWrapper><Batches /></SuspenseWrapper> },
      { path: 'schedule', element: <SuspenseWrapper><AdminSchedule /></SuspenseWrapper> },
      { path: 'tests', element: <SuspenseWrapper><AdminTests /></SuspenseWrapper> },
      { path: 'create-test', element: <SuspenseWrapper><CreateTest /></SuspenseWrapper> },
      { path: 'bulk-upload', element: <SuspenseWrapper><AdminBulkUploadQuestionsAndTests /></SuspenseWrapper> },
      { path: 'reports', element: <SuspenseWrapper><AdminReports /></SuspenseWrapper> },
      { path: 'attempts/:attemptId', element: <SuspenseWrapper><AdminAttemptDetail /></SuspenseWrapper> },
      { path: 'analytics', element: <SuspenseWrapper><Analytics /></SuspenseWrapper> },
      { path: 'billing', element: <SuspenseWrapper><AdminBilling /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><AdminProfile /></SuspenseWrapper> },
      { path: 'audit-log', element: <SuspenseWrapper><AuditLog /></SuspenseWrapper> },
      { path: 'attendance', element: <SuspenseWrapper><AdminAttendance /></SuspenseWrapper> },
      { path: 'accountants', element: <SuspenseWrapper><AdminAccountants /></SuspenseWrapper> },
      // Same finance pages the accountant uses (backend already authorizes
      // admin on every /finance/* route) — lets the admin monitor billed vs
      // collected, see who hasn't paid, and record a payment directly
      // themselves if needed, under an /admin/finance/* path so it doesn't
      // collide with the unrelated /admin/students (academic) page.
      { path: 'finance/students', element: <SuspenseWrapper><StudentFees /></SuspenseWrapper> },
      { path: 'finance/reports', element: <SuspenseWrapper><CollectionsReport /></SuspenseWrapper> },
      { path: 'finance/receipts', element: <SuspenseWrapper><Receipts /></SuspenseWrapper> },
      { path: 'finance/pending-dues', element: <SuspenseWrapper><PendingDues /></SuspenseWrapper> },
      { path: 'finance/corrections', element: <SuspenseWrapper><PaymentCorrections /></SuspenseWrapper> },
    ],
  },

  // Accountant Routes (Finance module)
  {
    path: '/accountant',
    element: <AccountantLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><AccountantDashboard /></SuspenseWrapper> },
      { path: 'fee-structures', element: <SuspenseWrapper><FeeStructures /></SuspenseWrapper> },
      { path: 'students', element: <SuspenseWrapper><StudentFees /></SuspenseWrapper> },
      { path: 'reports', element: <SuspenseWrapper><CollectionsReport /></SuspenseWrapper> },
      { path: 'receipts', element: <SuspenseWrapper><Receipts /></SuspenseWrapper> },
      { path: 'pending-dues', element: <SuspenseWrapper><PendingDues /></SuspenseWrapper> },
      { path: 'corrections', element: <SuspenseWrapper><PaymentCorrections /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><AccountantProfile /></SuspenseWrapper> },
    ],
  },

  // Superadmin Routes
  {
    path: '/superadmin',
    element: <SuperAdminLayout />,
    children: [
      { path: 'dashboard', element: <SuspenseWrapper><SuperAdminDashboard /></SuspenseWrapper> },
      { path: 'customers', element: <SuspenseWrapper><Customers /></SuspenseWrapper> },
      { path: 'customers/:id', element: <SuspenseWrapper><CustomerDetail /></SuspenseWrapper> },
      { path: 'instructors', element: <SuspenseWrapper><Instructors /></SuspenseWrapper> },
      { path: 'instructors/:id', element: <SuspenseWrapper><InstructorDetail /></SuspenseWrapper> },
      { path: 'billing', element: <SuspenseWrapper><Billing /></SuspenseWrapper> },
      { path: 'curriculum', element: <SuspenseWrapper><AdminCurriculum /></SuspenseWrapper> },
      { path: 'curriculum-analytics', element: <SuspenseWrapper><CurriculumAnalytics /></SuspenseWrapper> },
      { path: 'archive', element: <SuspenseWrapper><ArchivedData /></SuspenseWrapper> },
      { path: 'admissions', element: <SuspenseWrapper><Admissions /></SuspenseWrapper> },
      // MINTT registrations live in Admissions now; old links land there.
      { path: 'mintt-registrations', element: <Navigate to="/superadmin/admissions?source=mintt" replace /> },
      { path: 'individual-students', element: <SuspenseWrapper><IndividualStudents /></SuspenseWrapper> },
      { path: 'individual-courses', element: <SuspenseWrapper><IndividualCourses /></SuspenseWrapper> },
      { path: 'individual-payments', element: <SuspenseWrapper><IndividualPayments /></SuspenseWrapper> },
      { path: 'questions', element: <SuspenseWrapper><QuestionBank key="bank-1" bank={1} /></SuspenseWrapper> },
      { path: 'questions/pending-review', element: <SuspenseWrapper><PendingQuestionReview key="bank-1" bank={1} /></SuspenseWrapper> },
      { path: 'questions/add', element: <SuspenseWrapper><AddQuestion /></SuspenseWrapper> },
      { path: 'questions/:id/edit', element: <SuspenseWrapper><EditQuestion /></SuspenseWrapper> },
      // Question Bank 2 — a separate, independent copy of the bank (same pages, bank=2).
      { path: 'questions-2', element: <SuspenseWrapper><QuestionBank key="bank-2" bank={2} /></SuspenseWrapper> },
      { path: 'questions-2/pending-review', element: <SuspenseWrapper><PendingQuestionReview key="bank-2" bank={2} /></SuspenseWrapper> },
      { path: 'questions-2/add', element: <SuspenseWrapper><AddQuestion /></SuspenseWrapper> },
      { path: 'questions-2/:id/edit', element: <SuspenseWrapper><EditQuestion /></SuspenseWrapper> },
      { path: 'ebooks', element: <SuspenseWrapper><SuperAdminEBooks /></SuspenseWrapper> },
      { path: 'question-papers', element: <SuspenseWrapper><SuperAdminQuestionPapers /></SuspenseWrapper> },
      { path: 'inquiries', element: <SuspenseWrapper><Inquiries /></SuspenseWrapper> },
      { path: 'whatsapp-test', element: <SuspenseWrapper><WhatsAppTest /></SuspenseWrapper> },
      { path: 'whatsapp-templates', element: <SuspenseWrapper><WhatsAppTemplates /></SuspenseWrapper> },
      { path: 'whatsapp-accounts', element: <SuspenseWrapper><WhatsAppAccounts /></SuspenseWrapper> },
      { path: 'whatsapp-numbers', element: <SuspenseWrapper><WhatsAppNumbers /></SuspenseWrapper> },
      { path: 'whatsapp-reports', element: <SuspenseWrapper><WhatsAppReports /></SuspenseWrapper> },
      { path: 'permissions', element: <SuspenseWrapper><Permissions /></SuspenseWrapper> },
      { path: 'users', element: <SuspenseWrapper><SuperAdminUsers /></SuspenseWrapper> },
      { path: 'activity-log', element: <SuspenseWrapper><PlatformAuditLog /></SuspenseWrapper> },
      { path: 'whatsapp-dashboard', element: <SuspenseWrapper><WhatsAppDashboard /></SuspenseWrapper> },
      { path: 'support/tickets', element: <SuspenseWrapper><SupportTickets /></SuspenseWrapper> },
      { path: 'support/tickets/:id', element: <SuspenseWrapper><SupportTicketDetail /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><SuperAdminProfile /></SuspenseWrapper> },
    ],
  },

  // Catch-all
  {
    path: '*',
    element: <RedirectToHome />,
  },
]);

function App() {
  React.useEffect(() => {
    // A successful mount means chunks loaded fine — clear the flag so a future
    // deploy-while-tab-open can self-heal with a reload again, not just once ever.
    sessionStorage.removeItem(RELOAD_FLAG);
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <RouterProvider router={router} />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
